// ABOUTME: Editor for the global (cross-country) catalog metadata — DisplayName/Description + reference events,
// ABOUTME: units of measure and calculation methods per entity type. Backed by GET/PUT /tuc-configs/global-configuration.
import {Content} from '@hconnect/uikit'
import {Button, PageHeader} from '@hm/fundamental'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from '@mui/material'
import React, {useEffect, useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'

import {StringListEditor} from './components/StringListEditor'
import {UomPicker} from './components/UomPicker'
import {GlobalImportExportActions} from './components/GlobalImportExportActions'
import {useGlobalConfig, useUnitsOfMeasure, useUpsertGlobalConfig} from './hooks/useCatalogConfig'
import {
  GLOBAL_ENTITIES,
  type GlobalConfigurationOptions,
  type GlobalEntityFieldSpec,
  type GlobalEntityKey,
  type GlobalEntityMetadata,
  type UomEntry,
} from './types'

const clone = (config: GlobalConfigurationOptions): GlobalConfigurationOptions =>
  JSON.parse(JSON.stringify(config)) as GlobalConfigurationOptions

const uomIds = (uoms?: UomEntry[]) => (uoms ?? []).map((u) => u.id)
const toUoms = (ids: string[]): UomEntry[] => ids.map((id) => ({id}))

const Field: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div className="mt-3">
    <div className="mb-1 text-sm font-medium text-foreground">{label}</div>
    {children}
  </div>
)

export const GlobalConfigurationEditor: React.FC = () => {
  const history = useHistory()
  const {data, isLoading, error} = useGlobalConfig()
  const upsert = useUpsertGlobalConfig()
  const {data: uomOptions = []} = useUnitsOfMeasure()

  const [draft, setDraft] = useState<GlobalConfigurationOptions | null>(null)
  useEffect(() => {
    if (data) setDraft(clone(data))
  }, [data])

  const isDirty = useMemo(
    () => Boolean(draft && data && JSON.stringify(draft) !== JSON.stringify(data)),
    [draft, data]
  )

  const [leaveOpen, setLeaveOpen] = useState(false)

  const guardBack = () => {
    if (isDirty) setLeaveOpen(true)
    else history.push('/catalog-configurations')
  }

  const updateEntity = (key: GlobalEntityKey, patch: Partial<GlobalEntityMetadata>) => {
    if (!draft) return
    setDraft({...draft, [key]: {...draft[key], ...patch}})
  }

  const onSave = () => {
    if (draft) upsert.mutate(draft)
  }

  if (isLoading)
    return (
      <Content>
        <Paper elevation={4} style={{padding: '24px 32px'}}>Loading…</Paper>
      </Content>
    )
  if (error)
    return (
      <Content>
        <Paper elevation={4} style={{padding: '24px 32px'}} role="alert">
          <div className="text-error">{String((error as Error)?.message ?? error)}</div>
        </Paper>
      </Content>
    )
  if (!draft) return null

  const renderLists = (key: GlobalEntityKey, meta: GlobalEntityMetadata, spec: GlobalEntityFieldSpec) => (
    <>
      {spec.referenceEvents && (
        <Field label="Available reference events">
          <StringListEditor
            values={meta.availableReferenceEvents ?? []}
            onChange={(v) => updateEntity(key, {availableReferenceEvents: v})}
            placeholder="e.g. beforeRequestedDeliveryTime"
            data-cy={`global-${key}-refevents`}
          />
        </Field>
      )}
      {spec.unitsOfMeasure && (
        <Field label="Available units of measure">
          <UomPicker
            options={uomOptions}
            selected={uomIds(meta.availableUnitsOfMeasure)}
            onChange={(ids) => updateEntity(key, {availableUnitsOfMeasure: toUoms(ids)})}
            data-cy={`global-${key}-uom`}
          />
        </Field>
      )}
      {spec.calculationMethods && (
        <Field label="Available calculation methods">
          <StringListEditor
            values={meta.availableCalculationMethods ?? []}
            onChange={(v) => updateEntity(key, {availableCalculationMethods: v})}
            placeholder="e.g. basePlusAllModifiers"
            data-cy={`global-${key}-calc`}
          />
        </Field>
      )}
      {spec.distanceUnitsOfMeasure && (
        <Field label="Available distance units of measure">
          <UomPicker
            options={uomOptions}
            selected={uomIds(meta.availableDistanceUnitsOfMeasure)}
            onChange={(ids) => updateEntity(key, {availableDistanceUnitsOfMeasure: toUoms(ids)})}
            data-cy={`global-${key}-distuom`}
          />
        </Field>
      )}
      {spec.leadTimeUnitsOfMeasure && (
        <Field label="Available lead-time units of measure">
          <UomPicker
            options={uomOptions}
            selected={uomIds(meta.availableLeadTimeUnitsOfMeasure)}
            onChange={(ids) => updateEntity(key, {availableLeadTimeUnitsOfMeasure: toUoms(ids)})}
            data-cy={`global-${key}-leaduom`}
          />
        </Field>
      )}
      {spec.sizeUnitsOfMeasure && (
        <Field label="Available size units of measure">
          <UomPicker
            options={uomOptions}
            selected={uomIds(meta.availableSizeUnitsOfMeasure)}
            onChange={(ids) => updateEntity(key, {availableSizeUnitsOfMeasure: toUoms(ids)})}
            data-cy={`global-${key}-sizeuom`}
          />
        </Field>
      )}
      {spec.hoseUnitsOfMeasure && (
        <Field label="Available hose units of measure">
          <UomPicker
            options={uomOptions}
            selected={uomIds(meta.availableHoseUnitsOfMeasure)}
            onChange={(ids) => updateEntity(key, {availableHoseUnitsOfMeasure: toUoms(ids)})}
            data-cy={`global-${key}-hoseuom`}
          />
        </Field>
      )}
    </>
  )

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader className="pl-0 pr-0 md:pl-0 md:pr-0">Global catalog metadata</PageHeader>
      </div>
      <Paper elevation={4} style={{padding: '24px 32px'}} data-cy="page-global-configuration-editor">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            className="text-sm text-foreground_faded hover:underline"
            onClick={guardBack}
          >
            ← Catalog configurations
          </button>
          <div className="flex items-center gap-2">
            <GlobalImportExportActions />
            {upsert.isError && (
              <span className="text-sm text-error">
                {String((upsert.error as Error)?.message ?? 'Save failed')}
              </span>
            )}
            {upsert.isSuccess && !isDirty && <span className="text-sm text-success">Saved</span>}
            <button
              type="button"
              className="rounded-md px-3 py-1.5 text-sm text-foreground_faded hover:underline disabled:opacity-50"
              onClick={() => data && setDraft(clone(data))}
              disabled={!isDirty || upsert.isLoading}
              data-cy="discard"
            >
              Discard
            </button>
            <Button onClick={onSave} disabled={!isDirty || upsert.isLoading} data-cy="save">
              {upsert.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="divide-y divide-foreground_faded rounded-md border border-foreground_faded">
          {GLOBAL_ENTITIES.map(({key, label, fields}) => {
            const meta = draft[key]
            return (
              <div key={key} className="p-4" data-cy={`global-entity-${key}`}>
                <h3 className="mb-2 font-medium text-foreground">{label}</h3>
                <div className="flex flex-col gap-3 md:flex-row">
                  <TextField
                    label="Display name"
                    size="small"
                    fullWidth
                    value={meta.displayName}
                    onChange={(e) => updateEntity(key, {displayName: e.target.value})}
                    data-cy={`global-${key}-displayname`}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    value={meta.description}
                    onChange={(e) => updateEntity(key, {description: e.target.value})}
                    data-cy={`global-${key}-description`}
                  />
                </div>
                {renderLists(key, meta, fields)}
              </div>
            )
          })}
        </div>
      </Paper>

      <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>You have unsaved edits. Leaving now will discard them.</DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={() => setLeaveOpen(false)}>
            Keep editing
          </Button>
          <Button
            onClick={() => {
              setLeaveOpen(false)
              history.push('/catalog-configurations')
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  )
}
